import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

interface NavItem {
  label: string;
  href: string;
  external?: boolean;
}

const navItems: NavItem[] = [
  { label: '首頁', href: '/' },
  { label: '品牌介紹', href: '/about' },
  { label: '合作通路', href: '/stores' },
  { label: '常見問題', href: '/faq' },
  { label: '閱讀推薦', href: '/blog' },
  { label: '書籍資料庫', href: 'https://catalog.sonbook.net/', external: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const renderNavLink = (item: NavItem, isActive: boolean, isMobile: boolean) => {
    const baseClasses = `${isMobile ? 'px-3 py-2.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap' : 'px-4 py-2 rounded-md transition-all duration-300 ease-out whitespace-nowrap'} font-medium`;

    const activeClasses = scrolled
      ? 'text-primary-600 bg-primary-50'
      : 'text-accent-400 bg-background-50/15';

    const inactiveClasses = scrolled
      ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
      : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10';

    const sizeClasses = isMobile ? '' : (scrolled ? 'text-xs' : 'text-sm');
    const shadowStyle = scrolled ? undefined : { textShadow: '0 1px 6px rgba(0,0,0,0.45)' };

    const className = `${baseClasses} ${sizeClasses} ${isActive ? activeClasses : inactiveClasses}`;

    if (item.external) {
      return (
        <a
          key={item.href}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className} inline-flex items-center gap-1`}
          style={shadowStyle}
        >
          <LocalizedNavLabel href={item.href} fallback={item.label} />
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-external-link-line text-[10px]" />
          </span>
        </a>
      );
    }

    return (
      <Link
        key={item.href}
        to={item.href}
        className={className}
        style={shadowStyle}
      >
        <LocalizedNavLabel href={item.href} fallback={item.label} />
      </Link>
    );
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out ${
        scrolled
          ? 'py-2 bg-[rgba(10,10,10,0.15)] backdrop-blur-[16px] -webkit-backdrop-blur-[16px]'
          : 'py-3 bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div
          className={`flex items-center justify-between transition-all duration-300 ease-out ${
            scrolled ? 'h-12' : 'h-16'
          }`}
        >
          <Link to="/" className="flex items-center gap-3 whitespace-nowrap">
            <span
              className={`font-heading font-bold tracking-tight transition-all duration-300 ease-out ${
                scrolled ? 'text-lg' : 'text-xl'
              } ${
                scrolled ? 'text-foreground-900' : 'text-background-50'
              }`}
              style={scrolled ? undefined : { textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.35)' }}
            >
              <span className={`transition-colors duration-300 ease-out ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>財經</span><span className="text-accent-500">錢線</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = !item.external && location.pathname === item.href;
              return renderNavLink(item, isActive, false);
            })}
          </div>

          <LanguageSwitcher />

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${
              scrolled ? 'text-foreground-800' : 'text-background-50'
            }`}
            style={scrolled ? undefined : { textShadow: '0 1px 6px rgba(0,0,0,0.45)' }}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden px-4 pb-4 border-b ${
          scrolled
            ? 'bg-[rgba(10,10,10,0.15)] backdrop-blur-[16px] -webkit-backdrop-blur-[16px] border-background-200/20'
            : 'bg-[rgba(10,10,10,0.08)] backdrop-blur-[12px] -webkit-backdrop-blur-[12px] border-background-50/10'
        }`}>
          <div className="flex flex-col gap-1 pt-2">
            {navItems.map((item) => {
              const isActive = !item.external && location.pathname === item.href;
              return renderNavLink(item, isActive, true);
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
